import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RiRefreshLine,
  RiArrowDownSLine,
  RiMapPinLine,
  RiRadioButtonLine,
  RiMegaphoneLine,
} from '@remixicon/react';
import { useSearchStore } from '../store/searchStore';
import { useLang } from '../i18n/translations';
import { localizePlace } from '../i18n/places';

import { fetchDestinations } from '../services/sogralApi';
import { searchLiveDepartures, type LiveSearchDeparture } from '../services/liveSearchService';
import { recordSnapshot, getVelocity, type VelocityReading } from '../services/velocityTracker';
import { useRunPrediction } from '../hooks/usePrediction';
import { DepartureTicket } from '../components/live/DepartureTicket';
import { RecommendationBanner } from '../components/results/RecommendationBanner';
import { SyncIndicator } from '../components/shared/SyncIndicator';
import { DestinationCombobox } from '../components/search/DestinationCombobox';
import { cn } from '../lib/utils';
import type { TimeSlot, DayPrediction } from '../types';

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function nextWeekday(dayIndex: number): string {
  const today = new Date();
  const diff = ((dayIndex - today.getDay() + 7) % 7) || 7;
  const target = new Date(today);
  target.setDate(today.getDate() + diff);
  return target.toISOString().split('T')[0];
}

const CHIP_TO_JS_DAY = [-1, 0, 1, 2, 3, 4, 5, 6];


function AdSlot({ width, height, sticky = false }: { width: number; height: number; sticky?: boolean }) {
  return (
    <div
      className={cn(
        'w-full rounded-xl border-2 border-dashed border-line-light bg-white',
        'flex flex-col items-center justify-center gap-2',
        sticky && 'lg:sticky lg:top-6',
      )}
      style={{ maxWidth: width, height }}
    >
      <div className="w-9 h-9 rounded-xl bg-surface-ui flex items-center justify-center">
        <RiMegaphoneLine className="h-4 w-4 text-line" />
      </div>
      <span className="font-display text-[11px] font-semibold text-line uppercase tracking-widest">
        Annonce
      </span>
      <span className="font-data text-[10px] text-line-light">{width}×{height}</span>
    </div>
  );
}


function TicketSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-start justify-between mb-3">
          <div className="h-8 w-20 rounded bg-gray-100" />
          <div className="h-5 w-24 rounded-full bg-gray-100" />
        </div>
        <div className="flex items-center gap-2">
          <div className="shrink-0">
            <div className="h-4 w-14 rounded bg-gray-100 mb-1.5" />
            <div className="h-3 w-8 rounded bg-gray-100" />
          </div>
          <div className="h-4 w-4 rounded-full bg-gray-100 shrink-0" />
          <div className="flex-1 h-px bg-gray-100" />
          <div className="h-4 w-4 rounded-full bg-gray-100 shrink-0" />
          <div className="h-4 w-16 rounded bg-gray-100" />
        </div>
      </div>
      <div className="relative h-px mx-0">
        <div className="absolute inset-x-4 h-px bg-gray-100" />
        <span className="absolute -left-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-surface-page" />
        <span className="absolute -right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-surface-page" />
      </div>
      <div className="px-5 pt-3.5 pb-4">
        <div className="h-4 w-32 rounded bg-gray-100 mb-2" />
        <div className="h-4 w-24 rounded bg-gray-100" />
      </div>
    </div>
  );
}

function SlotSkeletonGroup() {
  return (
    <div className="space-y-4">
      {['a', 'b'].map((k) => (
        <div key={k}>
          <div className="h-4 w-16 rounded bg-gray-100 animate-pulse mb-2" />
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-9 w-[68px] rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


export function HomePage() {
  const {
    agencyId, destinationId, destinations, date,
    prediction, loading: predLoading, error: predError,
    setDestinations, setDestinationId, setDate,
  } = useSearchStore();
  const { t } = useLang();

  const [selectedChip, setSelectedChip] = useState(0);
  const [liveResults, setLiveResults] = useState<LiveSearchDeparture[] | null>(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  const [velocities, setVelocities] = useState<Record<string, VelocityReading | null>>({});
  const hasResults = useRef(false);

  const runPrediction = useRunPrediction();

  useEffect(() => {
    if (destinations.length > 0) return;
    fetchDestinations(agencyId).then(setDestinations).catch(() => {});
  }, [agencyId, destinations.length, setDestinations]);

  const fetchLive = useCallback(async (opts?: { background?: boolean }) => {
    if (!destinationId) return;
    const bg = opts?.background ?? false;
    if (!bg) setLiveLoading(true);
    setLiveError(null);
    try {
      const deps = await searchLiveDepartures({
        agencyId,
        destinationCode: destinationId,
        date: todayStr(),
        fresh: true,
      });
      const now = Date.now();
      for (const d of deps) recordSnapshot(d.id, d.seatsSold, now);
      const vmap: Record<string, VelocityReading | null> = {};
      for (const d of deps) vmap[d.id] = getVelocity(d.id, now);
      setVelocities(vmap);
      setLiveResults(deps);
      setFetchedAt(new Date());
      hasResults.current = true;
    } catch {
      if (!bg) {
        setLiveError(t.live.errorMsg);
        hasResults.current = false;
      }
    } finally {
      if (!bg) setLiveLoading(false);
    }
  }, [agencyId, destinationId, t.live.errorMsg]);

  useEffect(() => {
    if (!destinationId) return;
    setLiveResults(null);
    hasResults.current = false;
    if (selectedChip === 0) {
      setDate(todayStr());
      fetchLive();
    } else {
      setDate(nextWeekday(CHIP_TO_JS_DAY[selectedChip]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationId, selectedChip]);

  useEffect(() => {
    if (!destinationId || selectedChip === 0) return;
    runPrediction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runPrediction, destinationId]);

  useEffect(() => {
    if (selectedChip !== 0) return;
    const id = window.setInterval(() => {
      if (!document.hidden && hasResults.current) fetchLive({ background: true });
    }, 30_000);
    const onVisible = () => {
      if (!document.hidden && hasResults.current) fetchLive({ background: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchLive, selectedChip]);

  const selectedDest = destinations.find((d) => d.P1 === destinationId);
  const isToday = selectedChip === 0;
  const dayChips = [t.search.today, ...t.search.dayFull];

  const DestCombobox = (
    <DestinationCombobox
      destinations={destinations}
      value={destinationId}
      onChange={setDestinationId}
      placeholder={t.search.chooseDest}
      variant="dark"
    />
  );

  return (
    <div className="min-h-screen bg-surface-page">

      <div className="lg:hidden relative bg-brand">
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
          <img
            src="/new_assets/Pattern.svg"
            aria-hidden draggable={false}
            className="absolute -top-16 -right-16 w-[280px]"
            style={{ opacity: 0.22 }}
          />
        </div>
        <div className="relative z-10 px-4 py-3 flex items-center gap-3">
          <img src="/new_assets/LogoH.svg" alt="idaNroh" className="h-8 w-auto shrink-0" draggable={false} />
          <div className="h-6 w-px bg-white/20 shrink-0" />
          <div className="flex-1 min-w-0">{DestCombobox}</div>
        </div>
      </div>

      <div className="hidden lg:block relative bg-brand">
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
          <img
            src="/new_assets/Pattern.svg"
            aria-hidden draggable={false}
            className="absolute -top-20 right-0 w-[480px]"
            style={{ opacity: 0.18 }}
          />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-10 py-5 flex items-center gap-8">
          <img src="/new_assets/LogoH.svg" alt="idaNroh" className="h-10 w-auto shrink-0" draggable={false} />
          <div className="h-8 w-px bg-white/20 shrink-0" />
          <div className="shrink-0">
            <p className="font-display font-bold text-[17px] text-white leading-tight">{t.home.whereGoing}</p>
            <div className="flex items-center gap-1 mt-0.5">
              <RiMapPinLine className="h-3.5 w-3.5 text-white/55 shrink-0" />
              <p className="font-display text-[12px] text-white/55">{t.home.departureFrom}</p>
            </div>
          </div>
          <div className="flex-1 max-w-[400px]">
            <DestinationCombobox
              destinations={destinations}
              value={destinationId}
              onChange={setDestinationId}
              placeholder={t.search.chooseDest}
              variant="dark"
            />
          </div>
        </div>
      </div>

      <div className="hidden lg:flex justify-center py-4 bg-surface-raised border-b border-line-light">
        <AdSlot width={728} height={90} />
      </div>

      <div className="max-w-[1160px] mx-auto">
        <div className="
          px-5 pt-5 pb-16
          lg:px-8 lg:pt-7 lg:pb-14
          lg:grid lg:grid-cols-[1fr_300px] lg:gap-8 lg:items-start
        ">

          <div className="max-w-[480px] mx-auto lg:max-w-none lg:mx-0">

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <img src="/new_assets/icons/calendar-schedule-line.svg" className="h-[18px] w-[18px] shrink-0" aria-hidden />
                <p className="font-display font-semibold text-[16px] text-ink">
                  {t.home.whichDay}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {dayChips.map((label, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedChip(i)}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[14px] font-display font-medium transition-all',
                      selectedChip === i
                        ? 'bg-brand-dark text-white shadow-sm'
                        : 'bg-white border border-line text-ink-secondary',
                    )}
                  >
                    {selectedChip === i && i === 0 && <RiRadioButtonLine className="h-3.5 w-3.5" />}
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {!destinationId && (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                  <img src="/new_assets/icons/flight-takeoff-line-1.svg" className="h-7 w-7" aria-hidden />
                </div>
                <p className="font-display text-[14px] text-ink-muted max-w-[240px] leading-relaxed">
                  {t.home.chooseDest}
                </p>
              </div>
            )}

            {isToday && destinationId && (
              <LiveSection
                results={liveResults}
                loading={liveLoading}
                error={liveError}
                fetchedAt={fetchedAt}
                onRefresh={() => fetchLive()}
                destLabel={selectedDest ? localizePlace(selectedDest.S1, 'fr') : ''}
                velocities={velocities}
              />
            )}

            {!isToday && destinationId && (
              <PredictionSection
                loading={predLoading}
                error={predError}
                prediction={prediction}
                date={date}
              />
            )}
          </div>

          <aside className="hidden lg:flex flex-col gap-5 sticky top-6">
            <AdSlot width={300} height={250} />
            <AdSlot width={300} height={600} />
          </aside>

        </div>
      </div>
    </div>
  );
}


function LiveSection({
  results, loading, error, fetchedAt, onRefresh, destLabel, velocities,
}: {
  results: LiveSearchDeparture[] | null;
  loading: boolean;
  error: string | null;
  fetchedAt: Date | null;
  onRefresh: () => void;
  destLabel: string;
  velocities: Record<string, VelocityReading | null>;
}) {
  const { t } = useLang();

  if (error) {
    return (
      <div className="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-sm font-display">
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        <SectionHeader icon={<img src="/new_assets/icons/flight-takeoff-line-1.svg" className="h-[18px] w-[18px]" aria-hidden />} label="Départs actuels" />
        <div className="grid gap-3 lg:grid-cols-2">
          <TicketSkeleton />
          <TicketSkeleton />
          <TicketSkeleton />
        </div>
      </div>
    );
  }

  if (!results) return null;

  if (results.length === 0) {
    return (
      <div className="p-6 rounded-2xl bg-white text-center text-sm font-display text-ink-muted border border-gray-100">
        {t.live.noResults}
      </div>
    );
  }

  const upcoming = results.filter((d) => !d.isPast && d.status !== 'annule');
  const past = results.filter((d) => d.isPast || d.status === 'annule');

  return (
    <div className="space-y-4">
      {upcoming.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <SectionHeader
              icon={<img src="/new_assets/icons/flight-takeoff-line-1.svg" className="h-[18px] w-[18px]" aria-hidden />}
              label={t.home.liveSection(upcoming.length)}
              sublabel={destLabel}
            />
            <div className="flex items-center gap-2">
              <SyncIndicator lastUpdated={fetchedAt} intervalSec={30} />
              <button
                onClick={onRefresh}
                className="p-1.5 rounded-full hover:bg-gray-100 text-ink-faint transition-colors"
                aria-label="Actualiser"
              >
                <RiRefreshLine className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            {upcoming.map((d) => <DepartureTicket key={d.id} departure={d} velocity={velocities[d.id]} />)}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <details className="group">
          <summary className="flex items-center gap-2 cursor-pointer list-none mb-3">
            <SectionHeader
              icon={<img src="/new_assets/icons/flight-takeoff-line-1.svg" className="h-[18px] w-[18px] opacity-40" aria-hidden />}
              label={t.home.pastSection(past.length)}
              muted
            />
            <RiArrowDownSLine className="h-4 w-4 text-ink-faint group-open:rotate-180 transition-transform ms-auto" />
          </summary>
          <div className="grid gap-3 lg:grid-cols-2 opacity-60">
            {past.map((d) => <DepartureTicket key={d.id} departure={d} velocity={velocities[d.id]} />)}
          </div>
        </details>
      )}
    </div>
  );
}


function PredictionSection({
  loading, error, prediction, date,
}: {
  loading: boolean;
  error: string | null;
  prediction: DayPrediction | null;
  date: string;
}) {
  const { t } = useLang();

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="h-16 rounded-2xl bg-gray-100 animate-pulse" />
        <SlotSkeletonGroup />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-sm font-display">
        {error}
      </div>
    );
  }

  if (!prediction) return null;

  const morning = prediction.slots.filter((s) => parseInt(s.time.split(':')[0], 10) < 12);
  const afternoon = prediction.slots.filter((s) => parseInt(s.time.split(':')[0], 10) >= 12);

  return (
    <div className="space-y-4">
      <RecommendationBanner dayLabel={prediction.day_label} slots={prediction.slots} date={date} />

      <div className="flex items-center gap-4">
        <LegendDot color="bg-brand" label={t.slots.legendLow} />
        <LegendDot color="bg-warn" label={t.slots.legendMedium} />
        <LegendDot color="bg-danger" label={t.slots.legendHigh} />
      </div>

      {morning.length > 0 && <SlotGroup label="Matinée" icon="/new_assets/icons/meteocons_time-morning-fill.svg" slots={morning} date={date} />}
      {afternoon.length > 0 && <SlotGroup label="Après-midi" icon="/new_assets/icons/meteocons_time-afternoon-fill.svg" slots={afternoon} date={date} />}

      <p className="text-[11px] font-display text-ink-faint leading-relaxed">
        {t.predict.disclaimer}
      </p>
    </div>
  );
}


function SectionHeader({
  icon, label, sublabel, muted = false,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  muted?: boolean;
}) {
  return (
    <div className={cn('flex items-center gap-2', muted && 'opacity-50')}>
      <span className="text-ink-secondary">{icon}</span>
      <p className="font-display font-semibold text-[16px] text-ink">
        {label}
        {sublabel && (
          <span className="font-normal text-[14px] text-ink-muted ms-1">· {sublabel}</span>
        )}
      </p>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-[12px] font-display text-ink-muted">
      <span className={cn('w-2 h-2 rounded-full inline-block', color)} />
      {label}
    </span>
  );
}

function SlotGroup({ label, icon, slots, date }: { label: string; icon?: string; slots: TimeSlot[]; date: string }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2.5">
        {icon && <img src={icon} className="h-4 w-4" aria-hidden />}
        <p className="font-display font-semibold text-[13px] text-ink-muted uppercase tracking-wide">
          {label}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {slots.map((slot) => <SlotChip key={slot.time} slot={slot} date={date} />)}
      </div>
    </div>
  );
}

function SlotChip({ slot, date }: { slot: TimeSlot; date: string }) {
  const [active, setActive] = useState(false);

  const isToday = date === new Date().toISOString().split('T')[0];
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const [h, m] = slot.time.split(':').map(Number);
  const past = isToday && (h * 60 + m) < nowMin;

  const chipColor: Record<TimeSlot['crowd_level'], string> = {
    low: 'bg-brand/12 text-brand border-brand/25',
    medium: 'bg-warn/12 text-warn border-warn/25',
    high: 'bg-danger/12 text-danger border-danger/25',
  };

  return (
    <button
      onClick={() => setActive(!active)}
      className={cn(
        'rounded-xl border px-3 py-2 font-data font-bold text-[13px] transition-all min-w-[64px] text-center',
        chipColor[slot.crowd_level],
        past && 'opacity-35 grayscale',
        active && 'opacity-100',
      )}
    >
      {slot.time}
      {slot.is_recommended && !past && (
        <span className="ms-1 text-[10px] font-display align-middle">✓</span>
      )}
      {active && (
        <span className="ms-1 text-[10px] font-display opacity-70">~{slot.crowd_score}%</span>
      )}
    </button>
  );
}
